import * as React from 'react';
import LinearProgress, { type LinearProgressProps } from '@mui/material/LinearProgress';
import CircularProgress, { type CircularProgressProps } from '@mui/material/CircularProgress';

export interface ProgressBarProps {
  /** react-toolbox: type of progress bar */
  type?: 'linear' | 'circular';
  /** react-toolbox: mode - determinate or indeterminate */
  mode?: 'determinate' | 'indeterminate';
  /** react-toolbox: progress value (0-100) */
  value?: number;
  /** react-toolbox: buffer value for linear progress */
  buffer?: number;
  /** Additional class name */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

/**
 * ProgressBar wrapper - maps react-toolbox ProgressBar to MUI LinearProgress/CircularProgress
 * - `type="circular"` uses CircularProgress
 * - `type="linear"` (default) uses LinearProgress
 * - `mode` maps directly to `variant`
 */
const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ type = 'linear', mode = 'indeterminate', value, buffer, className, style }, ref) => {
    const variant = mode === 'determinate' ? 'determinate' : 'indeterminate';

    if (type === 'circular') {
      const circularProps: CircularProgressProps = {
        variant,
        value,
        className,
        style,
      };
      return <CircularProgress ref={ref as React.Ref<HTMLSpanElement>} {...circularProps} />;
    }

    const linearProps: LinearProgressProps = {
      variant: buffer ? 'buffer' : variant,
      value,
      valueBuffer: buffer,
      className,
      style,
    };

    return <LinearProgress ref={ref} {...linearProps} />;
  },
);

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
